import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, EmptyState, Modal } from '@playerbase/ui';

import type { PlaytestSessionSentiment } from '@playerbase/types';

import {
  expirePlaytestSession,
  fetchPlaytestSessionSentiment,
  fetchSessionDetail,
  type SessionListRow,
} from '../../games/playtest-sessions-data.js';
import { SentimentDot } from '../../feedback/sentiment-dot.js';
import { useShell } from '../../shell/use-shell.js';

import { BuildMarkerLabel } from '../../games/build-marker-label.js';
import { SessionTimeline } from './session-timeline.js';
import { SessionStateBadge } from './state-badge.js';
import { useSessionTimeline } from './use-session-timeline.js';

interface SessionDetailPaneProps {
  sessionId: string | null;
  onClose?: () => void;
}

export function SessionDetailPane({
  sessionId,
  onClose,
}: SessionDetailPaneProps): JSX.Element {
  const { playtestSessionsVersion } = useShell();
  const [detail, setDetail] = useState<SessionListRow | null>(null);
  const [sentiment, setSentiment] = useState<PlaytestSessionSentiment | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [expireConfirmOpen, setExpireConfirmOpen] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [expireError, setExpireError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (sessionId === null) {
        setDetail(null);
        setSentiment(null);
        setDetailLoading(false);
        return;
      }
      setDetailLoading(true);
      setSentiment(null);
      const next = await fetchSessionDetail(sessionId);
      if (cancelled) return;
      setDetail(next);
      setDetailLoading(false);
      // Sentiment is a non-blocking overlay: fetch it after the detail so
      // the card paints immediately and the dot fills in when it lands.
      const nextSentiment = await fetchPlaytestSessionSentiment(sessionId);
      if (cancelled) return;
      setSentiment(nextSentiment);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, playtestSessionsVersion]);

  const { cards, loading: timelineLoading } = useSessionTimeline(sessionId);

  const handleExpire = useCallback(async (): Promise<void> => {
    if (sessionId === null) return;
    setExpiring(true);
    setExpireError(null);
    const result = await expirePlaytestSession(sessionId);
    setExpiring(false);
    if (!result.ok) {
      setExpireError(
        result.error === 'not_a_member'
          ? "You don't have permission to expire this session."
          : 'Could not expire the session. Please try again.',
      );
      return;
    }
    setDetail((current) => (current === null ? current : { ...current, session: result.session }));
    setExpireConfirmOpen(false);
  }, [sessionId]);

  const canExpire =
    detail !== null
    && (detail.session.state === 'in_progress' || detail.session.state === 'completed');

  if (sessionId === null) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 py-8">
        <EmptyState
          title="Pick a session"
          description="Choose a session in the middle column to see its timeline."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <header className="flex items-start gap-3 border-b border-border px-5 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Session
          </span>
          <h2 className="truncate font-display text-lg font-semibold text-text">
            {detail !== null
              ? (detail.testerDisplayName
                ?? `tester ${detail.session.discordUserId.slice(-4)}`)
              : 'Loading…'}
          </h2>
        </div>
        {onClose !== undefined ? (
          <Button variant="ghost" onClick={onClose} aria-label="Close session details">
            Close
          </Button>
        ) : null}
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        {detailLoading ? (
          <div className="text-sm text-text-muted">Loading…</div>
        ) : detail === null ? (
          <EmptyState title="Session not found" description="The session may have been deleted." />
        ) : (
          <>
            <DetailCard row={detail} sentiment={sentiment} />
            {canExpire ? (
              <div className="flex">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setExpireError(null);
                    setExpireConfirmOpen(true);
                  }}
                >
                  Mark as expired
                </Button>
              </div>
            ) : null}
          </>
        )}

        {timelineLoading ? (
          <div className="text-sm text-text-muted">Loading timeline…</div>
        ) : cards.length === 0 ? (
          <EmptyState
            title="No activity yet"
            description="When the SDK fires this playtest's event for this tester, you'll see it here."
          />
        ) : (
          <SessionTimeline cards={cards} />
        )}
      </div>

      <Modal
        open={expireConfirmOpen}
        onClose={() => {
          if (!expiring) {
            setExpireError(null);
            setExpireConfirmOpen(false);
          }
        }}
        labelledById="expire-session-title"
        busy={expiring}
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id="expire-session-title">Mark session as expired</Card.Title>
            <Card.Description>
              Use this when you think the tester has abandoned the run. The
              session stays visible in history; their responses are kept.
              You can&rsquo;t bring it back to in-progress afterwards.
            </Card.Description>
          </Card.Header>
          {expireError !== null ? (
            <Card.Content>
              <Alert tone="danger">{expireError}</Alert>
            </Card.Content>
          ) : null}
          <Card.Footer>
            <Button
              variant="ghost"
              onClick={() => {
                setExpireError(null);
                setExpireConfirmOpen(false);
              }}
              disabled={expiring}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleExpire()}
              disabled={expiring}
            >
              {expiring ? 'Expiring…' : 'Mark as expired'}
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </div>
  );
}

function DetailCard({
  row,
  sentiment,
}: {
  row: SessionListRow;
  sentiment: PlaytestSessionSentiment | null;
}): JSX.Element {
  const tester =
    row.testerDisplayName ?? `tester ${row.session.discordUserId.slice(-4)}`;
  const band = sentiment?.band ?? null;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface px-4 py-3 text-sm">
      <Field label="Tester" value={tester} />
      {band !== null ? (
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Overall
          </span>
          <span className="flex">
            <SentimentDot band={band} showLabel />
          </span>
        </div>
      ) : null}
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Build
        </span>
        <BuildMarkerLabel marker={row.buildMarker} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          State
        </span>
        <span className="flex">
          <SessionStateBadge state={row.session.state} />
        </span>
      </div>
      <Field label="Started" value={formatTime(row.session.startedAt)} />
      <Field label="Last activity" value={formatTime(row.session.lastActivityAt)} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        {label}
      </span>
      <span className="text-text">{value}</span>
    </div>
  );
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString();
}
